import { Calendar, CheckCircle, OpenInWindow } from 'iconoir-react'
import moment from 'moment'
import { ReactElement, ReactNode, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import CalendarPicker from 'app/components/marketing/CalendarPicker'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { dueDateAsDay, taskDueState } from 'app/lib/taskDueDate'
import type { TaskJson } from 'app/types/entities'

interface TaskListPopoverProps {
  /** Open tasks for the contact; sorted internally by due date ascending. */
  tasks: TaskJson[]
  contactId: string
  onComplete: (taskId: string) => void
  /** dueDateIso is noon-local of the picked calendar day (TasksInbox convention). */
  onReschedule: (taskId: string, dueDateIso: string) => void
  /**
   * Actually opens the contact drawer. The URL deep link alone is not enough on
   * the contacts page: its drawer-opening effect runs on mount only, so the page's
   * own open handler must be invoked after the params are set.
   */
  onOpenContact?: () => void
  triggerClassName?: string
  /** Trigger content — the badge group. */
  children: ReactNode
}

const DUE_TEXT_CLASS: Record<string, string> = {
  overdue: `text-red-600`,
  dueToday: `text-blue-600`,
  upcoming: `text-subtle`,
}

/**
 * Badge-click popover for the contacts-table Tasks cell: lists the contact's
 * open tasks with complete / reschedule / open-in-drawer actions per row.
 * Portals to body so it escapes the cell's overflow clipping; non-modal and
 * click-shielded so the row's drawer-opening click never fires.
 */
export function TaskListPopover({
  tasks,
  contactId,
  onComplete,
  onReschedule,
  onOpenContact,
  triggerClassName,
  children,
}: TaskListPopoverProps): ReactElement {
  const location = useLocation()
  const navigate = useNavigate()

  // Due date ascending; undated tasks sort last (dueDateAsDay(undefined) would
  // silently resolve to today, which is wrong in both directions).
  const sorted = useMemo(
    () =>
      [...tasks].sort((a, b) => {
        if (!a.dueDate) return b.dueDate ? 1 : 0
        if (!b.dueDate) return -1
        return dueDateAsDay(a.dueDate).valueOf() - dueDateAsDay(b.dueDate).valueOf()
      }),
    [tasks],
  )

  const openTask = (taskId: string) => {
    const params = new URLSearchParams(location.search)
    params.set(`drawer`, contactId)
    params.set(`highlightTaskId`, taskId)
    navigate(`${location.pathname}?${params.toString()}`)
    onOpenContact?.()
  }

  return (
    <Popover modal={false}>
      {/* stopPropagation: the table row's own click opens the contact drawer. */}
      <PopoverTrigger className={triggerClassName} onClick={(e) => e.stopPropagation()}>
        {children}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 gap-1 bg-surface-raised p-1.5" onClick={(e) => e.stopPropagation()}>
        {sorted.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            onComplete={() => onComplete(task.id!)}
            onReschedule={(iso) => onReschedule(task.id!, iso)}
            onOpen={() => openTask(task.id!)}
          />
        ))}
        {sorted.length === 0 && <div className="px-2 py-1.5 text-body-xs text-subtle">No open tasks</div>}
      </PopoverContent>
    </Popover>
  )
}

function TaskRow({
  task,
  onComplete,
  onReschedule,
  onOpen,
}: {
  task: TaskJson
  onComplete: () => void
  onReschedule: (dueDateIso: string) => void
  onOpen: () => void
}): ReactElement {
  const [calendarOpen, setCalendarOpen] = useState(false)
  const state = taskDueState(task)
  const dueLabel = task.dueDate ? dueDateAsDay(task.dueDate).format(`MMM D`) : `No due date`

  return (
    <div className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-surface-subtle">
      <button
        type="button"
        aria-label="Mark complete"
        title="Mark complete"
        onClick={onComplete}
        className="shrink-0 text-minimal transition-colors hover:text-green-600"
      >
        <CheckCircle className="h-4 w-4" strokeWidth={2} />
      </button>
      <span data-testid="task-popover-title" className="min-w-0 flex-1 truncate text-body-xs text-strong">
        {task.title}
      </span>
      <Popover modal={false} open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger
          aria-label="Change due date"
          title="Change due date"
          onClick={(e) => e.stopPropagation()}
          className={`flex shrink-0 items-center gap-1 rounded px-1 py-0.5 text-body-xs transition-colors hover:bg-surface-muted ${
            DUE_TEXT_CLASS[state] ?? `text-subtle`
          }`}
        >
          <Calendar className="h-3 w-3" strokeWidth={2} />
          {dueLabel}
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 bg-surface-raised p-2" onClick={(e) => e.stopPropagation()}>
          <CalendarPicker
            date={task.dueDate ? moment(dueDateAsDay(task.dueDate)).format() : moment().format()}
            onDateChange={(d: Date) => {
              setCalendarOpen(false)
              // Noon-local so the value lands on the intended UTC calendar day.
              onReschedule(moment(d).startOf(`day`).add(12, `hours`).toISOString())
            }}
            limitToMonth={false}
            allowPastDates
            allowFutureDates
          />
        </PopoverContent>
      </Popover>
      <button
        type="button"
        aria-label="Open task"
        title="Open in contact drawer"
        onClick={onOpen}
        className="shrink-0 text-minimal transition-colors hover:text-default"
      >
        <OpenInWindow className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  )
}
